// WallPartner.jsx - Shared component for displaying partner logos in wall view
import anime from 'animejs';
import React, { useEffect, useRef, useState } from 'react';

export const WallPartner = ({ wallData = {} }) => {
  const wallDropShadowStyle =
    wallData?.wallSettings?.dropShadowStyle ||
    'drop-shadow-[0_0_1rem_rgba(0,0,0,0.25)]';
  const isWallOnly = wallData?.isWallOnly || false;
  const isLandscapeOnly = wallData?.isLandscapeOnly || false;
  const addDebugStyle = wallData?.addDebugStyle || '';
  const messageTypeCurrent =
    wallData?.wallMessage?.postItem?.postTypeForWall || 'none';

  const transitionDuration = 'duration-250';

  // Hide partner logo when backgroundOnly is true (for QR code displays)
  const backgroundOnly = wallData?.wallMessage?.postItem?.backgroundOnly ?? false;
  const partnerShow = backgroundOnly
    ? false
    : (wallData?.wallSettings?.wallPartner?.partnerShow ?? true);
  const partnerFadeDuration =
    wallData?.wallSettings?.wallPartner?.partnerFadeDuration || 500;
  const partnerEffectTranslateYIn =
    wallData?.wallSettings?.wallPartner?.partnerEffectTranslateYIn || '0';
  const partnerEffectTranslateYOut =
    wallData?.wallSettings?.wallPartner?.partnerEffectTranslateYOut || '0';
  const partnerEffectTranslateXIn =
    wallData?.wallSettings?.wallPartner?.partnerEffectTranslateXIn || '0';
  const partnerEffectTranslateXOut =
    wallData?.wallSettings?.wallPartner?.partnerEffectTranslateXOut || '0';
  const partnerEffectScaleIn =
    wallData?.wallSettings?.wallPartner?.partnerEffectScaleIn || '1';
  const partnerEffectScaleOut =
    wallData?.wallSettings?.wallPartner?.partnerEffectScaleOut || '0.5';
  const partnerEffectBlur =
    wallData?.wallSettings?.wallPartner?.partnerEffectBlur || '25px';
  const partnerEffectEaseIn =
    wallData?.wallSettings?.wallPartner?.partnerEffectEaseIn || 'easeOutSine';
  const partnerEffectEaseOut =
    wallData?.wallSettings?.wallPartner?.partnerEffectEaseOut || 'easeOutCubic';

  const refPartner1 = useRef(null);
  const refPartner2 = useRef(null);

  const [srcPartner1, setSrcPartner1] = useState(null);
  const [srcPartner2, setSrcPartner2] = useState(null);

  const [isPartner1Visible, setIsPartner1Visible] = useState(false);

  useEffect(() => {
    if (!wallData?.wallPartner?.partnerLogo) return;

    if (isPartner1Visible) setSrcPartner2(wallData?.wallPartner?.partnerLogo);
    else setSrcPartner1(wallData?.wallPartner?.partnerLogo);
    setIsPartner1Visible((oldValue) => !oldValue);
  }, [wallData?.wallPartner?.partnerLogo]);

  // Handle crossfade when both sources are set
  useEffect(() => {
    // Only run crossfade when both sources are set
    if (!srcPartner1 || !srcPartner2) return;

    const inPartner = isPartner1Visible ? refPartner1.current : refPartner2.current;
    const outPartner = isPartner1Visible ? refPartner2.current : refPartner1.current;

    if (!inPartner) return;

    anime({
      targets: inPartner,
      opacity: 1,
      translateY: [partnerEffectTranslateYIn, '0'],
      translateX: [partnerEffectTranslateXIn, '0'],
      scale: [partnerEffectScaleIn, 1],
      filter: ['blur(' + partnerEffectBlur + ')', 'blur(0px)'],
      easing: partnerEffectEaseIn,
      duration: partnerFadeDuration,
      begin: () => {
        anime({
          targets: outPartner,
          opacity: 0,
          translateY: ['0', partnerEffectTranslateYOut],
          translateX: ['0', partnerEffectTranslateXOut],
          scale: [1, partnerEffectScaleOut],
          filter: ['blur(0px)', 'blur(' + partnerEffectBlur + ')'],
          easing: partnerEffectEaseOut,
          duration: partnerFadeDuration,
        });
      },
    });
  }, [isPartner1Visible, srcPartner1, srcPartner2]);

  const wallPartnerStyle = `z-30 absolute transition-all ${partnerShow ? "opacity-100" : "opacity-0"} ` + transitionDuration;
  const wallPartnerLayoutStyle =
    messageTypeCurrent == 'post' || messageTypeCurrent == 'feel'
      ? isWallOnly
        ? 'top-1/32 h-3/16 left-31/32 w-3/16 -translate-x-full'
        : isLandscapeOnly
          ? 'top-1/16 h-3/16 left-1/32 w-3/16 -translate-x-0'
          : 'top-2/16 h-3/16 left-1/2 w-4/16 -translate-x-1/2'
      : messageTypeCurrent == 'snap'
        ? isWallOnly
          ? 'top-1/32 h-3/16 left-31/32 w-3/16 -translate-x-full'
          : isLandscapeOnly
            ? 'top-1/16 h-3/16 left-1/32 w-3/16 -translate-x-0'
            : 'top-2/16 h-3/16 left-1/2 w-4/16 -translate-x-1/2'
        : messageTypeCurrent == 'vote'
          ? isWallOnly
            ? 'top-1/32 h-3/16 left-1/32 w-3/16 -translate-x-0'
            : isLandscapeOnly
              ? 'top-1/16 h-3/16 left-1/32 w-3/16 -translate-x-0'
              : 'top-2/16 h-3/16 left-layout w-4/16 translate-x-0'
          : '';
  const wallPartnerSubStyle = 'absolute size-full';
  const wallPartnerMediaStyle =
    'object-contain size-full transition-all ' + transitionDuration;
  const wallPartnerMediaLayoutStyle =
    messageTypeCurrent == 'post' || messageTypeCurrent == 'feel'
      ? isWallOnly
        ? 'object-right'
        : isLandscapeOnly
          ? 'object-left'
          : 'object-center'
      : messageTypeCurrent == 'snap'
        ? isWallOnly
          ? 'object-right'
          : isLandscapeOnly
            ? 'object-left'
            : 'object-center'
        : messageTypeCurrent == 'vote'
          ? isWallOnly
            ? 'object-left'
            : isLandscapeOnly
              ? 'object-left'
              : 'object-left'
          : '';

  return (
    <div
      id="wallPartner"
      className={[
        wallPartnerStyle,
        wallPartnerLayoutStyle,
        wallDropShadowStyle,
        addDebugStyle,
      ].join(' ')}
    >
      <div
        id="wallPartner1"
        ref={refPartner1}
        className={[wallPartnerSubStyle, srcPartner2 ? 'opacity-0' : ''].join(' ')}
      >
        <div className="size-full">
          <img
            className={[
              wallPartnerMediaStyle,
              wallPartnerMediaLayoutStyle,
            ].join(' ')}
            src={srcPartner1}
            alt=""
          />
        </div>
      </div>

      <div
        id="wallPartner2"
        ref={refPartner2}
        className={[wallPartnerSubStyle, srcPartner1 ? 'opacity-0' : ''].join(' ')}
      >
        <div className="size-full">
          <img
            className={[
              wallPartnerMediaStyle,
              wallPartnerMediaLayoutStyle,
            ].join(' ')}
            src={srcPartner2}
            alt=""
          />
        </div>
      </div>
    </div>
  );
};
